import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { useLang } from "@/i18n";

export default function About() {
  const [expanded, setExpanded] = useState(false);
  const { t } = useLang();
  const chapters = [
    { num: "01", title: t("about.c1.t"), text: t("about.c1.x") },
    { num: "02", title: t("about.c2.t"), text: t("about.c2.x") },
    { num: "03", title: t("about.c3.t"), text: t("about.c3.x") },
    { num: "04", title: t("about.c4.t"), text: t("about.c4.x") },
  ];

  return (
    <section id="apropos" data-testid="about-section" className="py-28 lg:py-40 bg-[#0A0A0C]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="grid lg:grid-cols-12 gap-12 mb-24"
        >
          <p className="lg:col-span-3 text-[#C5A059] text-xs uppercase tracking-[0.4em]">{t("about.eyebrow")}</p>
          <div className="lg:col-span-9">
            <h2 className="font-display text-4xl md:text-5xl uppercase font-bold tracking-tight leading-tight">
              {t("about.title.a")} <span className="text-[#C5A059]">{t("about.title.b")}</span>
            </h2>
            <p className="text-lg md:text-xl leading-relaxed text-[#A1A1AA] mt-8 max-w-3xl">
              {t("about.text")}
            </p>

            <AnimatePresence initial={false}>
              {expanded && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden"
                >
                  <p className="text-base leading-relaxed text-[#A1A1AA] mt-8 max-w-3xl whitespace-pre-line border-l-2 border-[#C5A059]/50 pl-6" data-testid="about-story">
                    {t("about.story")}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              onClick={() => setExpanded(!expanded)}
              data-testid="about-toggle-story"
              className="group inline-flex items-center gap-3 mt-8 border border-[#C5A059]/60 text-[#C5A059] px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300"
            >
              {expanded ? <Minus size={14} /> : <Plus size={14} className="transition-transform duration-300 group-hover:rotate-90" />}
              {expanded ? t("about.less") : t("about.more")}
            </button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-px bg-[#27272A]">
          {chapters.map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#0A0A0C] p-10 lg:p-14 group hover:bg-[#121214] transition-colors duration-500"
              data-testid={`manifesto-chapter-${c.num}`}
            >
              <span className="font-editorial italic text-[#C5A059] text-lg">{c.num}</span>
              <h3 className="font-display text-2xl md:text-3xl uppercase font-medium mt-4 group-hover:text-[#C5A059] transition-colors duration-500">
                {c.title}
              </h3>
              <p className="text-base leading-relaxed text-[#71717A] mt-4">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
